import { useMemo } from 'react';
import { trpc } from '@/api/trpc';
import type { ScreenerRuleRow } from '@/types/appRouter';
import { useAppQuery } from './useAppQuery';

export function useScreenerRuleOptions(currentScreenerRuleId: number | null, config?: { enabled?: boolean; staleTime?: number }) {
    const enabled = config?.enabled ?? true;

    const listQuery = useAppQuery({
        ...trpc.screener.list.queryOptions(),
        enabled,
        ...(config?.staleTime !== undefined ? { staleTime: config.staleTime } : {})
    });

    const isRuleInList = useMemo(() => {
        if (currentScreenerRuleId == null) return true;
        return (listQuery.data?.rules ?? []).some((r) => r.id === currentScreenerRuleId);
    }, [currentScreenerRuleId, listQuery.data?.rules]);

    const metaQuery = useAppQuery({
        ...trpc.screener.meta.queryOptions({ screenerRuleId: currentScreenerRuleId ?? 0 }),
        enabled: enabled && currentScreenerRuleId != null && listQuery.isFetched && !isRuleInList
    });

    // Listed rules plus the currently selected rule when it is absent from the list
    const rules = useMemo<ScreenerRuleRow[]>(() => {
        const listedRules = listQuery.data?.rules ?? [];
        const selectedMetaRule = metaQuery.data?.rule ?? null;
        const selectedMetaRuleIsMissing = selectedMetaRule ? !listedRules.some((r) => r.id === selectedMetaRule.id) : false;

        return selectedMetaRuleIsMissing && selectedMetaRule ? [...listedRules, selectedMetaRule] : listedRules;
    }, [listQuery.data?.rules, metaQuery.data?.rule]);

    return {
        rules,
        listQuery,
        metaQuery
    };
}
